import { useState } from 'react'
import {
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Copy,
  Globe,
  RotateCw,
  X,
  XCircle,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { ProgressBar, Spinner } from '@/components/ui/misc'
import { formatBytes, formatDuration, formatRate } from '@/lib/format'
import { useUploads, type UploadItem } from './UploadProvider'

export function UploadQueue() {
  const { items, cancel, retry, dismiss, clearFinished } = useUploads()
  const [collapsed, setCollapsed] = useState(false)

  const visible = items.filter((item) => item.status !== 'conflict')
  if (visible.length === 0) return null

  const active = visible.filter((i) => i.status === 'uploading' || i.status === 'queued').length
  const done = visible.filter((i) => i.status === 'completed').length
  const finished = visible.filter((i) =>
    ['completed', 'failed', 'cancelled'].includes(i.status),
  ).length

  return (
    <div className="fixed bottom-4 right-4 z-30 w-[min(26rem,calc(100vw-2rem))] overflow-hidden rounded-lg border border-border bg-background shadow-xl">
      <div className="flex items-center justify-between gap-2 border-b border-border bg-surface px-4 py-2.5">
        <div className="flex items-center gap-2 text-sm font-medium">
          {active > 0 && <Spinner />}
          <span>
            {active > 0
              ? `Uploading ${active} file${active === 1 ? '' : 's'}`
              : `${done} upload${done === 1 ? '' : 's'} finished`}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {finished > 0 && active === 0 && (
            <Button size="sm" variant="ghost" onClick={clearFinished}>
              Clear
            </Button>
          )}
          <Button
            size="icon"
            variant="ghost"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'Expand upload list' : 'Collapse upload list'}
          >
            {collapsed ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {!collapsed && (
        <ul className="max-h-80 divide-y divide-border overflow-y-auto">
          {visible.map((item) => (
            <QueueRow
              key={item.id}
              item={item}
              onCancel={() => cancel(item.id)}
              onRetry={() => retry(item.id)}
              onDismiss={() => dismiss(item.id)}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function QueueRow({
  item,
  onCancel,
  onRetry,
  onDismiss,
}: {
  item: UploadItem
  onCancel: () => void
  onRetry: () => void
  onDismiss: () => void
}) {
  const [copied, setCopied] = useState(false)

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard may be blocked; the field is selectable as a fallback.
    }
  }

  const percent = item.totalBytes > 0 ? (item.uploadedBytes / item.totalBytes) * 100 : 0
  const remainingBytes = Math.max(0, item.totalBytes - item.uploadedBytes)
  const eta = item.rateBps > 0 ? remainingBytes / item.rateBps : 0

  return (
    <li className="space-y-2 px-4 py-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium" title={item.finalKey ?? item.key}>
            {item.file.name}
          </p>
          {item.finalKey && item.finalKey !== item.key && (
            <p className="truncate text-xs text-muted-foreground">saved as {item.finalKey}</p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {item.status === 'completed' && <CheckCircle2 className="h-4 w-4 text-success" />}
          {item.status === 'failed' && <XCircle className="h-4 w-4 text-danger" />}

          {item.status === 'failed' && (
            <Button size="icon" variant="ghost" onClick={onRetry} aria-label="Retry upload">
              <RotateCw className="h-4 w-4" />
            </Button>
          )}
          {(item.status === 'uploading' || item.status === 'queued') && (
            <Button size="icon" variant="ghost" onClick={onCancel} aria-label="Cancel upload">
              <X className="h-4 w-4" />
            </Button>
          )}
          {['completed', 'failed', 'cancelled'].includes(item.status) && (
            <Button size="icon" variant="ghost" onClick={onDismiss} aria-label="Dismiss">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {item.status === 'uploading' && <ProgressBar value={percent} />}

      {/* Permanent public URL, shown as soon as the ACL is applied. */}
      {item.status === 'completed' && item.publicUrl && (
        <div className="flex items-center gap-1.5 rounded-md border border-border bg-surface px-2 py-1.5">
          <Globe className="h-3.5 w-3.5 shrink-0 text-warning" />
          <input
            readOnly
            value={item.publicUrl}
            onFocus={(e) => e.currentTarget.select()}
            className="min-w-0 flex-1 bg-transparent font-mono text-[11px] outline-none"
            aria-label="Public link"
          />
          <button
            onClick={() => void copy(item.publicUrl as string)}
            className="shrink-0 rounded p-1 hover:bg-muted"
            aria-label="Copy public link"
            title="Copy public link"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-success" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </button>
        </div>
      )}

      {item.status === 'completed' && item.publicError && (
        <p className="text-xs text-danger">
          Uploaded, but could not be made public: {item.publicError}
        </p>
      )}

      <p className="text-xs text-muted-foreground tabular">
        {item.status === 'uploading' && (
          <>
            {formatBytes(item.uploadedBytes)} / {formatBytes(item.totalBytes)}
            {item.rateBps > 0 && <> · {formatRate(item.rateBps)}</>}
            {eta > 0 && <> · {formatDuration(eta)} left</>}
          </>
        )}
        {item.status === 'queued' && <>Waiting · {formatBytes(item.totalBytes)}</>}
        {item.status === 'completed' && <>Uploaded · {formatBytes(item.totalBytes)}</>}
        {item.status === 'cancelled' && <>Cancelled</>}
        {item.status === 'failed' && <span className="text-danger">{item.error}</span>}
      </p>
    </li>
  )
}
